function persistLayerVisibility(orgId, layers) {
  const visibility = {}
  layers.forEach(l => {
    if (l.properties?.visible) visibility[l.uuid] = true
  })
  localStorage.setItem(`layerVisibility_${orgId}`, JSON.stringify(visibility))
}

function getSavedLayerVisibility(orgId) {
  const saved = localStorage.getItem(`layerVisibility_${orgId}`)
  if (!saved) return null
  try {
    const parsed = JSON.parse(saved)
    return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : null
  } catch (e) {
    console.warn('Failed to parse layer visibility from localStorage:', e)
    return null
  }
}

export const mutations = {
  SET_LAYERS(state, payload) {
    state.layers = state.layers.concat(payload)
  },
  UPDATE_LAYER(state, payload) {
    state.layers = state.layers.map(layer => {
      if (layer.uuid === payload.uuid) {
        return payload
      }
      return layer
    })
  },
  // Organization layers mutations
  SET_ORG_LAYERS(state, { orgId, layers }) {
    const existingLayers = state.orgLayers[orgId] || []
    const existingLayersMap = new Map(existingLayers.map(layer => [layer.uuid, layer]))

    const mergedLayers = layers.map(newLayer => {
      const existingLayer = existingLayersMap.get(newLayer.uuid)

      if (existingLayer && existingLayer.properties) {
        // Preserve existing client-side state when merging
        const preserved = {}
        if (existingLayer.properties.features) {
          preserved.features = existingLayer.properties.features
          preserved.featureCollection = existingLayer.properties.featureCollection || {
            type: 'FeatureCollection',
            features: existingLayer.properties.features
          }
        }
        if (existingLayer.properties.hiddenCaptures) {
          preserved.hiddenCaptures = existingLayer.properties.hiddenCaptures
        }
        if (existingLayer.properties.visible !== undefined) {
          preserved.visible = existingLayer.properties.visible
        }

        if (Object.keys(preserved).length > 0) {
          return {
            ...newLayer,
            properties: {
              ...newLayer.properties,
              ...preserved
            }
          }
        }
      }

      return newLayer
    })

    // Order comes from server (sort_order column). Restore visibility only.
    let orderedLayers = mergedLayers

    // Restore saved visibility from localStorage on fresh page load
    const visibilityMap = getSavedLayerVisibility(orgId)
    if (visibilityMap && existingLayers.length === 0) {
      orderedLayers = orderedLayers.map(l => {
        if (visibilityMap[l.uuid]) {
          return { ...l, properties: { ...l.properties, visible: true } }
        }
        return l
      })
    }

    state.orgLayers = {
      ...state.orgLayers,
      [orgId]: orderedLayers
    }
  },
  ADD_ORG_LAYER(state, { orgId, layer }) {
    if (!state.orgLayers[orgId]) {
      state.orgLayers[orgId] = []
    }
    state.orgLayers[orgId] = [layer, ...state.orgLayers[orgId]]
  },
  UPDATE_ORG_LAYER(state, { orgId, layer }) {
    if (state.orgLayers[orgId]) {
      const replace = (layers) => layers.map(l => {
        if (l.uuid === layer.uuid) return layer
        const children = l.properties?.children
        if (!children?.length) return l
        const nextChildren = replace(children)
        if (nextChildren.every((c, i) => c === children[i])) return l
        return { ...l, properties: { ...l.properties, children: nextChildren } }
      })
      state.orgLayers[orgId] = replace(state.orgLayers[orgId])
      persistLayerVisibility(orgId, state.orgLayers[orgId])
    }
  },
  UPDATE_ORG_LAYER_FEATURES(state, { orgId, layerId, features }) {
    if (state.orgLayers[orgId]) {
      // Extract thumbUrl from resources for each feature (for image markers)
      const processedFeatures = features.map(f => {
        const resources = f.properties?.resources
        const thumbUrl = resources?.find(r => r.url?.includes('small'))?.url
                      || resources?.find(r => r.url?.includes('thumbnail'))?.url
        if (thumbUrl) {
          return {
            ...f,
            properties: {
              ...f.properties,
              thumbUrl
            }
          }
        }
        return f
      })

      state.orgLayers[orgId] = state.orgLayers[orgId].map(l => {
        if (l.uuid === layerId) {
          return {
            ...l,
            properties: {
              ...l.properties,
              features: processedFeatures,
              featureCollection: {
                type: 'FeatureCollection',
                features: processedFeatures
              }
            }
          }
        }
        return l
      })
    }
  },
  RENAME_ORG_LAYER(state, { orgId, layerId, name }) {
    if (state.orgLayers[orgId]) {
      state.orgLayers[orgId] = state.orgLayers[orgId].map(l => {
        if (l.uuid === layerId) {
          return {
            ...l,
            properties: {
              ...l.properties,
              name
            }
          }
        }
        return l
      })
    }
  },
  DELETE_ORG_LAYER(state, { orgId, layerId }) {
    if (state.orgLayers[orgId]) {
      state.orgLayers[orgId] = state.orgLayers[orgId].filter(layer => layer.uuid !== layerId)
      persistLayerVisibility(orgId, state.orgLayers[orgId])
    }
  },
  MOVE_ORG_LAYER_UP(state, { orgId, layerId }) {
    if (state.orgLayers[orgId]) {
      const layers = [...state.orgLayers[orgId]]
      const index = layers.findIndex(layer => layer.uuid === layerId)
      if (index > 0) {
        const temp = layers[index - 1]
        layers[index - 1] = layers[index]
        layers[index] = temp
        state.orgLayers = { ...state.orgLayers, [orgId]: layers }
      }
    }
  },
  MOVE_ORG_LAYER_DOWN(state, { orgId, layerId }) {
    if (state.orgLayers[orgId]) {
      const layers = [...state.orgLayers[orgId]]
      const index = layers.findIndex(layer => layer.uuid === layerId)
      if (index >= 0 && index < layers.length - 1) {
        const temp = layers[index + 1]
        layers[index + 1] = layers[index]
        layers[index] = temp
        state.orgLayers = { ...state.orgLayers, [orgId]: layers }
      }
    }
  },
  REORDER_ORG_LAYERS(state, { orgId, layers }) {
    state.orgLayers = { ...state.orgLayers, [orgId]: layers }
  },
  REORDER_CHILDREN(state, { orgId, parentUuid, children }) {
    if (!state.orgLayers[orgId]) return
    state.orgLayers = {
      ...state.orgLayers,
      [orgId]: state.orgLayers[orgId].map(l =>
        l.uuid === parentUuid
          ? { ...l, properties: { ...l.properties, children } }
          : l
      )
    }
  },
  ADD_DATES(state, payload) {
    try {
      const existingDatesMap = new Map();

      if (state.dates && Array.isArray(state.dates)) {
        state.dates.forEach(date => {
          if (date && date.id) {
            existingDatesMap.set(date.id, date);
          }
        });
      }

      const payloadArray = Array.isArray(payload) ? payload : [payload];
      payloadArray.forEach(newDate => {
        if (!newDate || typeof newDate !== 'object') return;

        if (newDate.id === 'features' && newDate.layers && Array.isArray(newDate.layers)) {
          newDate.layers.forEach(templateLayer => {
            if (templateLayer && templateLayer.id) {
              const safeTemplateLayer = {
                id: templateLayer.id,
                name: templateLayer.name || 'Unknown Template',
                color: templateLayer.color || 'blue',
                dates: Array.isArray(templateLayer.dates) ? [...templateLayer.dates] : []
              };
              existingDatesMap.set(templateLayer.id, safeTemplateLayer);
            }
          });
        }
        else if (newDate.id) {
          const safeDateObject = {
            id: newDate.id,
            name: newDate.name || newDate.id || 'Unknown',
            color: newDate.color || 'blue',
            dates: Array.isArray(newDate.dates) ? [...newDate.dates] : []
          };
          existingDatesMap.set(newDate.id, safeDateObject);
        }
      });

      state.dates = Array.from(existingDatesMap.values());
    } catch (err) {
      console.error('Error in ADD_DATES mutation:', err);
    }
  },
  CLEAR_DATES(state) {
    state.dates = [];
  },
  REMOVE_DATES(state, dateIds) {
    if (!Array.isArray(dateIds) || !state.dates) return
    const idsToRemove = new Set(dateIds)
    state.dates = state.dates.filter(date => !idsToRemove.has(date.id))
  },
  SET_SELECTED_RANGE(state, payload) {
    state.selectedRange = payload
  },
  SET_SELECTED_FEATURE(state, { feature, coordinates, isVisible }) {
    state.selectedFeature = {
      feature,
      coordinates,
      isVisible
    }
  },
  UPDATE_LAYER_PROPERTIES(state, { orgId, layerId, properties }) {
    if (state.orgLayers[orgId]) {
      state.orgLayers[orgId] = state.orgLayers[orgId].map(l => {
        if (l.uuid === layerId) {
          return {
            ...l,
            properties: {
              ...l.properties,
              ...properties
            }
          }
        }
        return l
      })
      if ('visible' in properties) {
        persistLayerVisibility(orgId, state.orgLayers[orgId])
      }
    }
  },
  SET_ACTIVE_RESULT(state, { layerId, result }) {
    state.activeResults = {
      ...state.activeResults,
      [layerId]: result
    }
  },
  SET_SUBSCRIPTION_RESULTS(state, { orgId, results }) {
    state.subscriptionResults = {
      ...state.subscriptionResults,
      [orgId]: results
    }
  },
  SET_PLANETSCOPE_SELECTION(state, { layerId, selection }) {
    state.planetscopeSelection = {
      ...state.planetscopeSelection,
      [layerId]: selection
    }
  },
  SET_MAP_VIEW_SETTINGS(state, { mapId, settings }) {
    state.mapViewSettings = {
      ...state.mapViewSettings,
      [mapId]: settings
    }
  },
  SET_ORDER_PRODUCT_BUNDLE(state, { layerId, bundle }) {
    state.orderProductBundle = {
      ...state.orderProductBundle,
      [layerId]: bundle
    }
  },
  SET_PLANET_VARS_SELECTION(state, { layerId, selection }) {
    state.planetVarsSelection = {
      ...state.planetVarsSelection,
      [layerId]: selection
    }
  },
  SET_LULC_ACTIVE_MONTH(state, { parentCode, monthCode }) {
    state.lulcActiveMonths = {
      ...state.lulcActiveMonths,
      [parentCode]: monthCode
    }
  },
  ADD_TEMP_LAYER(state, { orgId, layer }) {
    const existing = state.tempLayers[orgId] || []
    state.tempLayers = {
      ...state.tempLayers,
      [orgId]: [...existing, layer]
    }
  },
  REMOVE_TEMP_LAYER(state, { orgId, layerId }) {
    if (!state.tempLayers[orgId]) return
    state.tempLayers = {
      ...state.tempLayers,
      [orgId]: state.tempLayers[orgId].filter(l => l.uuid !== layerId)
    }
  },
  UPDATE_TEMP_LAYER(state, { orgId, layer }) {
    if (!state.tempLayers[orgId]) return
    state.tempLayers = {
      ...state.tempLayers,
      [orgId]: state.tempLayers[orgId].map(l => l.uuid === layer.uuid ? layer : l)
    }
  },
  TOGGLE_LAYER_SELECTION(state, { orgId, layerCode }) {
    const current = state.selectedLayers[orgId] || {}
    if (current[layerCode]) {
      const { [layerCode]: _, ...rest } = current
      state.selectedLayers = { ...state.selectedLayers, [orgId]: rest }
    } else {
      state.selectedLayers = { ...state.selectedLayers, [orgId]: { ...current, [layerCode]: true } }
    }
  },
  CLEAR_LAYER_SELECTION(state, { orgId }) {
    state.selectedLayers = { ...state.selectedLayers, [orgId]: {} }
  },
  SET_CLIPBOARD_LAYER(state, { orgId, layerCode, name }) {
    state.clipboardLayer = { orgId, layerCode, name }
  },
  CLEAR_CLIPBOARD_LAYER(state) {
    state.clipboardLayer = null
  }
}
